import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { RotateCcw, Check, X, ExternalLink, RefreshCw } from 'lucide-react';
import { getApiUrl } from '../../utils/api';

const API_URL = getApiUrl();
const SYM = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
const money = (v, cur) => `${SYM[cur] || cur} ${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-800', refunded: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800', cancelled: 'bg-slate-100 text-slate-600', closed_dispute: 'bg-purple-100 text-purple-800',
};
const REASON_CODES = ['USAGE_TOO_HIGH', 'SUSPECTED_ABUSE', 'PAYMENT_UNDER_DISPUTE', 'OTHER'];

const DecisionModal = ({ req, onClose, onDone }) => {
  const [decision, setDecision] = useState('approved');
  const [note, setNote] = useState('');
  const [reasonCode, setReasonCode] = useState('OTHER');
  const [refundId, setRefundId] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { decision, note: note.trim() || undefined };
      if (decision === 'rejected') body.reason_code = reasonCode;
      if (decision === 'approved' && refundId.trim()) body.refund_id = refundId.trim();
      const res = await axios.post(`${API_URL}/api/refund-requests/${req.request_id}/decision`, body, { withCredentials: true, timeout: 60000 });
      toast.success(decision === 'approved' ? `Refunded ${money(res.data.refunded_amount, req.currency)} · ${res.data.refund_id}` : 'Request rejected — learner notified');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Decision failed');
      if (err.response?.status === 424) onDone();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="refund-decision-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Decide refund · {req.name || req.email}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="refund-decision-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-sm bg-slate-50 rounded p-3 space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">Paid</span><span>{money(req.paid_amount, req.currency)}</span></div>
          <div className="flex justify-between font-semibold"><span className="text-slate-500">Refund exactly</span><span data-testid="refund-decision-amount">{money(req.refund_amount, req.currency)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Gateway · original payment</span><span className="font-mono text-xs">{req.gateway} · {req.payment_id}</span></div>
        </div>
        <div className="flex gap-2">
          {['approved', 'rejected'].map((d) => (
            <button key={d} type="button" onClick={() => setDecision(d)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border ${decision === d ? (d === 'approved' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-rose-600 text-white border-rose-600') : 'bg-white text-slate-700 border-slate-200'}`}
              data-testid={`refund-decision-${d}`}>{d === 'approved' ? 'Approve & refund' : 'Reject'}</button>
          ))}
        </div>
        {decision === 'approved' ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-600 bg-slate-50 rounded p-2">Razorpay refund of {money(req.refund_amount, req.currency)} is executed automatically against {req.payment_id}; LMS is notified only after Razorpay accepts it.</p>
            <input value={refundId} onChange={(e) => setRefundId(e.target.value)} placeholder="Already refunded in the Razorpay dashboard? Paste the rfnd_… id (optional)" className="input-base text-sm" data-testid="refund-decision-refund-id" />
          </div>
        ) : (
          <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} className="input-base text-sm" data-testid="refund-decision-reason-code">
            {REASON_CODES.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
          </select>
        )}
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="input-base text-sm"
          placeholder={decision === 'rejected' ? 'Note shown to the learner (required)' : 'Internal note (optional)'} data-testid="refund-decision-note" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || (decision === 'rejected' && !note.trim())}
            className={`text-sm px-4 py-2 rounded-lg font-medium text-white disabled:opacity-50 ${decision === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
            data-testid="refund-decision-submit">{busy ? 'Working…' : decision === 'approved' ? 'Confirm refund' : 'Confirm rejection'}</button>
        </div>
      </div>
    </div>
  );
};

const RefundRequestsTab = ({ onPendingCount }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState({ requests: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState(null);

  const syncRazorpayRefunds = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API_URL}/api/payments/sync-refunds?days=90`, {}, { withCredentials: true, timeout: 180000 });
      setSyncReport(res.data);
      toast.success(res.data.applied > 0
        ? `${res.data.applied} refund(s) pulled into the leads' transaction history`
        : 'No new refunds — everything Razorpay has is already recorded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Razorpay refund sync failed');
    } finally { setSyncing(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/refund-requests`, { params: { status }, withCredentials: true });
      setData(res.data);
      onPendingCount?.(res.data.counts?.pending || 0);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not load refund requests'); }
    finally { setLoading(false); }
  }, [status, onPendingCount]);

  useEffect(() => { load(); }, [load]);

  const openLead = (r) => navigate(`/leads?search=${encodeURIComponent(r.email || r.phone || r.name || '')}`);
  const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="space-y-4" data-testid="refund-requests-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1">
          {[['pending', 'Pending'], ['refunded,rejected', 'Decided'], ['cancelled,closed_dispute', 'Closed by LMS'], ['all', 'All']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setStatus(k)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border ${status === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'}`}
              data-testid={`refund-filter-${k.split(',')[0]}`}>
              {l}{k === 'pending' && data.counts?.pending ? ` (${data.counts.pending})` : ''}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={syncRazorpayRefunds} disabled={syncing} className="btn-secondary text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="sync-razorpay-refunds-btn">
            <RotateCcw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing…' : 'Sync Razorpay refunds'}
          </button>
          <button onClick={load} className="btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="refund-refresh-btn"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
        </div>
      </div>
      <p className="text-xs text-slate-500">Learner-initiated pro-rata refunds for self-serve SoulAI Coach / Baby Tracker (from the LMS app). The amount is frozen by LMS — refund exactly that amount or reject with a note. SLA: decision in 3 working days.</p>
      <p className="text-xs text-slate-500"><strong>Sync Razorpay refunds</strong> pulls anything refunded straight from the Razorpay dashboard into the lead's transaction history (last 90 days, idempotent). It also runs automatically every 30 minutes.</p>

      {syncReport && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-2" data-testid="refund-sync-report">
          <div className="flex flex-wrap gap-4">
            <span>Razorpay refunds scanned: <strong>{syncReport.razorpay_refunds}</strong></span>
            <span className="text-emerald-700">Newly synced: <strong>{syncReport.applied}</strong></span>
            <span className="text-slate-600">Already recorded: <strong>{syncReport.already_recorded}</strong></span>
            <span className={syncReport.unmatched?.length ? 'text-amber-700' : 'text-slate-600'}>No matching OPS payment: <strong>{syncReport.unmatched?.length || 0}</strong></span>
          </div>
          {syncReport.details?.length > 0 && (
            <div className="space-y-0.5">
              {syncReport.details.map((d) => (
                <div key={d.refund_id} className="font-mono text-[11px] text-slate-700">
                  {d.refund_id} · {money(d.amount, d.currency)} → lead {d.lead_id || '—'} {d.fully_refunded ? '(full)' : '(partial)'}
                </div>
              ))}
            </div>
          )}
          {syncReport.unmatched?.length > 0 && (
            <div className="space-y-0.5">
              <p className="text-amber-700">These refunds exist in Razorpay but the original payment is not in OPS — check the payment id:</p>
              {syncReport.unmatched.map((u) => (
                <div key={u.refund_id} className="font-mono text-[11px] text-amber-800">
                  {u.refund_id} · {money(u.amount, u.currency)} · {u.razorpay_payment_id}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Requested</th><th className="px-3 py-2">Lead</th><th className="px-3 py-2">Product</th>
              <th className="px-3 py-2 text-right">Paid</th><th className="px-3 py-2 text-right">Refund</th><th className="px-3 py-2">Usage</th>
              <th className="px-3 py-2">Reason</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.requests.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-500" data-testid="refund-empty">{loading ? 'Loading…' : 'No refund requests here.'}</td></tr>
            )}
            {data.requests.map((r) => (
              <tr key={r.request_id} data-testid={`refund-row-${r.request_id}`} className="hover:bg-slate-50">
                <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmt(r.requested_at)}</td>
                <td className="px-3 py-2">
                  <button type="button" onClick={() => openLead(r)} className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-1 text-left" data-testid={`refund-lead-link-${r.request_id}`}>
                    {r.lead_name || r.name || r.email} <ExternalLink className="w-3 h-3" />
                  </button>
                  <div className="text-xs text-slate-500">{r.email}{r.phone ? ` · ${r.phone}` : ''}{!r.lead_id && <span className="ml-1 text-amber-700">(no OPS lead)</span>}</div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{r.product === 'baby_tracker' ? 'Baby Tracker' : 'SoulAI Coach'} <span className="text-xs text-slate-500">· {r.plan}</span><div className="text-[11px] text-slate-400">{r.gateway}</div></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{money(r.paid_amount, r.currency)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap font-semibold" data-testid={`refund-amount-${r.request_id}`}>{money(r.refund_amount, r.currency)}</td>
                <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                  {r.quote?.used_days ?? '—'}/{r.quote?.total_days ?? '—'} days
                  {r.usage?.videos_completed !== undefined && <div>{r.usage.videos_completed} videos</div>}
                  {r.usage?.logs !== undefined && <div>{r.usage.logs} logs</div>}
                  {r.previous_refunds_365d > 0 && <div className="text-amber-700">{r.previous_refunds_365d} prior refund(s)</div>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-[200px] truncate" title={r.reason || ''}>{r.reason || '—'}{r.note && <div className="text-slate-400 truncate" title={r.note}>OPS: {r.note}</div>}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[r.status] || 'bg-slate-100 text-slate-600'}`} data-testid={`refund-status-${r.request_id}`}>{r.status}</span>
                  {r.refund_id && <div className="text-[11px] text-slate-400 font-mono">{r.refund_id}</div>}
                  {r.refund_id && r.lms_notified === false && <div className="text-[11px] text-amber-700">refunded · LMS not yet notified</div>}
                  {r.needs_attention && <div className="text-[11px] text-rose-700" title={r.attention_note} data-testid={`refund-attention-${r.request_id}`}>needs attention · LMS said {r.lms_decided_status}</div>}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.status === 'pending' && (
                    <button onClick={() => setDeciding(r)} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700" data-testid={`refund-decide-btn-${r.request_id}`}>
                      <RotateCcw className="w-3.5 h-3.5" /> Decide
                    </button>
                  )}
                  {r.status === 'refunded' && <Check className="w-4 h-4 text-emerald-600 inline" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {deciding && <DecisionModal req={deciding} onClose={() => setDeciding(null)} onDone={() => { setDeciding(null); load(); }} />}
    </div>
  );
};

export default RefundRequestsTab;
